// Tables on a sheet (Features 5, Fixes 9 H2b), kept as simple as Google Sheets' tables: a block of cells with a header
// row, named (Orders) so formulas can read a column by its header (=SUM(Orders[Total])) or the whole table's rows
// (=COUNTA(Orders)). A table keeps, for each of its columns, a formula and a number look set on any one row of it (a
// calculated column): a row typed, pasted or appended by an agent right under the table joins it and takes both, and a
// formula that read a whole column of the table (=SUM(D2:D9) under it) grows with it.
//
// Kept on the tab (SheetTabData.tables): its name, its block ("A1:D9", the header row first) and its columns' formulas
// and looks by their place in the table ("3": the fourth). A column's formula is kept as written for one row (at), so
// it is moved to any other row as a copy would be (moveFormula). The browser (src/boards/sheet/ops.ts), an agent's
// write_sheet (agent.ts) and the engine's names (bookFor, live.ts) all read these.

import { MAX_COLS, MAX_ROWS, addrOf, colName, parseRect, rectName, type Rect } from "./addr";
import { isFormula, refBox } from "./engine";
import { cleanFmt, type CellFmt } from "./format";
import { nameProblem } from "./names";
import type { Lines } from "./lines";
import { moveFormula, mapRefs, shiftFormula } from "./shift";
import { ParseError, tokenize } from "./parse";

/** Where a row or column of the tab goes when lines are put in or taken out: its new place, or null when it is taken out (lines.ts). */
export function lineAfter(i: number, l: Lines): number | null {
  if (l.del) return i < l.at ? i : i < l.at + l.n ? null : i - l.n;
  return i < l.at ? i : i + l.n;
}

export const MAX_TABLES = 20;

/** A table's column: the formula every row of it takes, as written for row `at` (from 0), and its number look. */
export interface TableCol {
  f?: string;
  at?: number;
  fmt?: CellFmt;
}

export interface SheetTable {
  name: string;
  /** its block, the header row first: "A1:D9" */
  range: string;
  /** each column's formula and look, by its place in the table from 0 ("3") */
  cols?: Record<string, TableCol>;
}

/** The looks a table's column keeps for every row (a type of value): the rest stay each cell's own. */
export const COLUMN_LOOKS = ["nf", "dp", "sym", "ds", "al"] as const;

/** A table's block: the header row and at least one row under it. */
export function tableRect(t: Pick<SheetTable, "range">): Rect | null {
  if (typeof t.range !== "string" || !/^[A-Z]{1,3}\d{1,5}:[A-Z]{1,3}\d{1,5}$/.test(t.range)) return null;
  const x = parseRect(t.range);
  return x && x.r1 > x.r0 ? x : null;
}

const meet = (a: Rect, b: Rect) => a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;

/** A tab's tables, each sound, none on another and each named once (the first kept); undefined for none. */
export function cleanTables(raw: unknown): SheetTable[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: SheetTable[] = [];
  const kept: Rect[] = [];
  for (const v of raw) {
    if (out.length >= MAX_TABLES || !v || typeof v !== "object") continue;
    const r = v as Record<string, unknown>;
    const name = typeof r.name === "string" ? r.name.trim() : "";
    const range = typeof r.range === "string" ? r.range.trim().replace(/\$/g, "").toUpperCase() : "";
    const x = tableRect({ range });
    if (!x || nameProblem(name) || kept.some((k) => meet(k, x)) || out.some((t) => t.name.toLowerCase() === name.toLowerCase())) continue;
    const cols: Record<string, TableCol> = {};
    if (r.cols && typeof r.cols === "object")
      for (const [k, c] of Object.entries(r.cols as Record<string, unknown>)) {
        const i = Number(k);
        if (!Number.isInteger(i) || i < 0 || i > x.c1 - x.c0 || !c || typeof c !== "object") continue;
        const cc = c as Record<string, unknown>;
        const col: TableCol = {};
        if (typeof cc.f === "string" && isFormula(cc.f) && cc.f.length <= 5000 && typeof cc.at === "number" && Number.isInteger(cc.at) && cc.at >= 0 && cc.at < MAX_ROWS) {
          col.f = cc.f;
          col.at = cc.at;
        }
        const look = cleanFmt(Object.fromEntries(COLUMN_LOOKS.filter((key) => cc.fmt && typeof cc.fmt === "object" && key in (cc.fmt as object)).map((key) => [key, (cc.fmt as Record<string, unknown>)[key]])), false);
        if (look) col.fmt = look;
        if (col.f || col.fmt) cols[String(i)] = col;
      }
    kept.push(x);
    out.push({ name, range: rectName(x), ...(Object.keys(cols).length ? { cols } : {}) });
  }
  return out.length ? out : undefined;
}

/** Each table of a tab with its block, worked out once for each list. */
const RECTS = new WeakMap<SheetTable[], { t: SheetTable; x: Rect }[]>();
export function tablesOf(list: SheetTable[] | undefined): { t: SheetTable; x: Rect }[] {
  if (!list?.length) return [];
  const kept = RECTS.get(list);
  if (kept) return kept;
  const out = list.flatMap((t) => {
    const x = tableRect(t);
    return x ? [{ t, x }] : [];
  });
  RECTS.set(list, out);
  return out;
}

/** The table a cell is in (its header row too), with the cell's column's place in it. */
export function tableAt(list: SheetTable[] | undefined, r: number, c: number): { t: SheetTable; x: Rect; i: number } | null {
  for (const e of tablesOf(list)) if (r >= e.x.r0 && r <= e.x.r1 && c >= e.x.c0 && c <= e.x.c1) return { ...e, i: c - e.x.c0 };
  return null;
}

/** The look a table gives a cell: its header bold, its body cells their column's number look. Undefined for none. */
export function tableLook(list: SheetTable[] | undefined, r: number, c: number): CellFmt | undefined {
  if (!list?.length) return undefined;
  const at = tableAt(list, r, c);
  if (!at) return undefined;
  if (r === at.x.r0) return HEAD;
  return at.t.cols?.[String(at.i)]?.fmt;
}
const HEAD: CellFmt = { b: 1 };

/** A header cell's words as a column's name: what was typed, else Column 1, Column 2, each name once. */
export function headerNames(t: SheetTable, raw: (r: number, c: number) => string): string[] {
  const x = tableRect(t);
  if (!x) return [];
  const out: string[] = [];
  for (let c = x.c0; c <= x.c1; c++) {
    let s = raw(x.r0, c).trim();
    if (s.startsWith("'")) s = s.slice(1);
    if (!s || isFormula(s)) s = `Column ${c - x.c0 + 1}`;
    s = s.replace(/[[\]]/g, "").replace(/\s+/g, " ").slice(0, 80);
    let name = s;
    for (let k = 2; out.some((o) => o.toLowerCase() === name.toLowerCase()); k++) name = `${s} ${k}`;
    out.push(name);
  }
  return out;
}

/**
 * The names a sheet's tables give its formulas, for the engine: each table's rows (Orders) and each column's
 * (Orders[Total]), as the cells under the header. raw reads a tab's cell as typed, for the headers.
 */
export function tableNames(tabs: { entry: { id: string }; data: { tables?: SheetTable[]; names?: Record<string, string> } }[], raw: (tabId: string, r: number, c: number) => string): { name: string; tabId: string; range: string }[] {
  const out: { name: string; tabId: string; range: string }[] = [];
  // each name once: a named range keeps its own, and of two tables with one name (an older copy) the first tab's
  const seen = new Set(tabs.flatMap((t) => Object.keys(t.data.names ?? {}).map((k) => k.toLowerCase())));
  for (const tab of tabs)
    for (const { t, x } of tablesOf(tab.data.tables)) {
      if (seen.has(t.name.toLowerCase())) continue;
      seen.add(t.name.toLowerCase());
      out.push({ name: t.name, tabId: tab.entry.id, range: rectName({ ...x, r0: x.r0 + 1 }) });
      headerNames(t, (r, c) => raw(tab.entry.id, r, c)).forEach((h, i) => out.push({ name: `${t.name}[${h}]`, tabId: tab.entry.id, range: rectName({ r0: x.r0 + 1, c0: x.c0 + i, r1: x.r1, c1: x.c0 + i }) }));
    }
  return out;
}

/** A table's column formula moved to a row, as a copy of it would be. */
export function columnFormula(col: TableCol | undefined, r: number): string | null {
  return col?.f && typeof col.at === "number" ? moveFormula(col.f, r - col.at, 0) : null;
}

/** A formula's references and names, read once: null when it is not a formula or does not parse. */
function tokensOf(raw: string): ReturnType<typeof tokenize> | null {
  if (!isFormula(raw)) return null;
  try {
    return tokenize(raw.slice(1));
  } catch (e) {
    if (e instanceof ParseError) return null;
    throw e;
  }
}

const onTab = (sheet: string | null, tabName: string | undefined) => sheet === null || (!!tabName && sheet.toLowerCase() === tabName.toLowerCase());
const namesTable = (v: string, t: SheetTable) => (v.includes("[") ? v.slice(0, v.indexOf("[")) : v).toLowerCase() === t.name.toLowerCase();

/**
 * Whether a formula totals a table's rows from rows 0..upTo: it reads a block of more than one of its rows in its
 * columns (=SUM(D2:D9)), or names the table (=SUM(Orders[Total])). A row of such formulas is a totals row, not a row of
 * the table.
 */
export function totalsFormula(raw: string, t: SheetTable, x: Rect, upTo: number, tabName?: string): boolean {
  const toks = tokensOf(raw);
  if (!toks) return false;
  for (const k of toks) {
    if (k.k === "name" && namesTable(k.v, t)) return true;
    if (k.k !== "ref" || !k.ref.b || k.ref.dead || !onTab(k.ref.sheet, tabName)) continue;
    const b = refBox(k.ref);
    if (!b || b.r1 <= b.r0) continue;
    if (b.r0 <= Math.min(upTo, x.r1) && b.r1 > x.r0 && b.c0 <= x.c1 && b.c1 >= x.c0) return true;
  }
  return false;
}

/** Whether a formula reads column c of its own tab at all (a cell or a block of it), or names the table. */
function readsColumn(raw: string, c: number, t: SheetTable, tabName?: string): boolean {
  const toks = tokensOf(raw);
  if (!toks) return true;
  for (const k of toks) {
    if (k.k === "name" && namesTable(k.v, t)) return true;
    if (k.k !== "ref" || k.ref.dead || !onTab(k.ref.sheet, tabName)) continue;
    const b = refBox(k.ref);
    if (b && b.c0 <= c && b.c1 >= c) return true;
  }
  return false;
}

/** A write to a cell, as tables see it: its place, what it puts there (undefined: leaves it), and its look. */
export interface TWrite {
  r: number;
  c: number;
  raw?: string | null;
  fmt?: CellFmt | null;
}

export interface TableChange {
  /** the tab's tables after, or null when none changed */
  tables: SheetTable[] | null;
  /** more cells to write: column formulas filled into rows */
  fill: TWrite[];
  /** each table that grew: its columns, the row its body starts on, and its last row before and after */
  grew: { name: string; c0: number; c1: number; body: number; from: number; to: number }[];
  /** columns whose formula changed, in words ("Total, =B2*C2") */
  learned: string[];
  /** filled cells a column's formula wrote over (they held the column's formula as it was), with what they held */
  over: { r: number; c: number; was: string }[];
}

/**
 * What writes to a tab do to its tables (Features 5): rows written right under a table, with the rows between empty in
 * its columns, join it (grow); every row that joins takes each column's formula where the write left its cell empty; and
 * a formula written in a table's column (learn: typed by the person, or an agent's cells) becomes the column's, filling
 * each row where the cell is empty or holds the column's formula as it was, never a value typed there.
 * raw reads a cell as typed now, before the writes.
 */
export function tableChange(list: SheetTable[] | undefined, writes: TWrite[], raw: (r: number, c: number) => string, opts: { grow: boolean; learn: boolean; tab?: string }): TableChange {
  const out: TableChange = { tables: null, fill: [], grew: [], learned: [], over: [] };
  if (!list?.length || !writes.length) return out;
  const written = new Map<number, TWrite>();
  for (const w of writes) written.set(w.r * 1024 + w.c, { ...written.get(w.r * 1024 + w.c), ...w });
  const after = (r: number, c: number) => {
    const w = written.get(r * 1024 + c);
    return w && w.raw !== undefined ? (w.raw ?? "") : raw(r, c);
  };
  let changed = false;
  const next = list.map((t): SheetTable => {
    const x = tableRect(t);
    if (!x) return t;
    let cols = t.cols ? { ...t.cols } : undefined;
    let r1 = x.r1;
    // a row whose formulas total the rows above it (=SUM(D2:D9), =SUM(Orders[Total])) is a totals row, not one of its
    const totalsAt = (r: number) => {
      for (let c = x.c0; c <= x.c1; c++) if (totalsFormula(after(r, c), t, x, r - 1, opts.tab)) return true;
      return false;
    };
    // rows written right under it, with nothing between in its columns, join it
    if (opts.grow) {
      let last = x.r1;
      for (const w of writes) if (w.raw && w.c >= x.c0 && w.c <= x.c1 && w.r > last) last = Math.max(last, w.r);
      if (last > x.r1) {
        let gap = false;
        for (let r = x.r1 + 1; r <= last && !gap; r++) {
          let any = false;
          for (let c = x.c0; c <= x.c1; c++) if (after(r, c)) any = true;
          // a row of the block with nothing in it after the write, or one that held something before it, ends the run
          let before = false;
          for (let c = x.c0; c <= x.c1; c++) if (raw(r, c) && !written.has(r * 1024 + c)) before = true;
          if (!any || before || totalsAt(r)) gap = true;
          else r1 = r;
        }
      }
    }
    // its last row written into a totals row (a label typed under it joined, then =SUM(D2:D9) beside it) leaves it
    if (r1 === x.r1 && x.r1 > x.r0 + 1 && writes.some((w) => w.r === x.r1 && w.c >= x.c0 && w.c <= x.c1) && totalsAt(x.r1)) {
      r1 = x.r1 - 1;
      changed = true;
    }
    // a formula written in a column of its body becomes the column's: never one from a row that joined in this change,
    // one that reads its own column (a total, a running sum) or names the table, or one that would read cells that
    // are not there on another row
    if (opts.learn)
      for (const w of writes) {
        if (!w.raw || !isFormula(w.raw) || w.r <= x.r0 || w.r > Math.min(r1, x.r1) || w.c < x.c0 || w.c > x.c1) continue;
        if (readsColumn(w.raw, w.c, t, opts.tab)) continue;
        if (moveFormula(w.raw, x.r0 + 1 - w.r, 0).includes("#REF!") || moveFormula(w.raw, r1 - w.r, 0).includes("#REF!")) continue;
        const i = String(w.c - x.c0);
        const was = cols?.[i];
        const before = columnFormula(was, w.r);
        if (before === w.raw) continue;
        cols = { ...(cols ?? {}), [i]: { ...(was ?? {}), f: w.raw, at: w.r } };
        changed = true;
        // the column by its header's words (the Total column), as the person reads it
        out.learned.push(`${after(x.r0, w.c).trim() || addrOf(x.r0, w.c)} column, ${moveFormula(w.raw, x.r0 + 1 - w.r, 0)}`);
        // every other row: empty, or the column's formula as it was
        for (let r = x.r0 + 1; r <= r1; r++) {
          if (r === w.r || written.has(r * 1024 + w.c)) continue;
          const cur = raw(r, w.c);
          if (cur && cur !== columnFormula(was, r)) continue;
          const next = moveFormula(w.raw, r - w.r, 0);
          // a cell that held the column's formula as it was is written over: said, and kept in the trash
          if (cur && cur !== next) out.over.push({ r, c: w.c, was: cur });
          out.fill.push({ r, c: w.c, raw: next });
        }
      }
    if (r1 > x.r1) {
      // the rows that joined take each column's formula where the write left the cell empty
      for (const [i, col] of Object.entries(cols ?? {})) {
        const c = x.c0 + Number(i);
        for (let r = x.r1 + 1; r <= r1; r++) {
          if (after(r, c) || out.fill.some((f) => f.r === r && f.c === c)) continue;
          const f = columnFormula(col, r);
          if (f) out.fill.push({ r, c, raw: f });
        }
      }
      out.grew.push({ name: t.name, c0: x.c0, c1: x.c1, body: x.r0 + 1, from: x.r1, to: r1 });
      changed = true;
    }
    if (!changed && r1 === x.r1) return t;
    return { ...t, range: rectName({ ...x, r1 }), ...(cols && Object.keys(cols).length ? { cols } : {}) };
  });
  if (changed) out.tables = next;
  return out;
}

/**
 * A formula with each block that read a whole column of a table that grew (its rows from the header's next to its last,
 * B2:B9) reading the grown rows too (B2:B12), as Excel's totals under a table do. Other references stay as they are.
 */
export function growFormula(raw: string, on: string, tab: string, g: TableChange["grew"][number]): string {
  return mapRefs(raw, (ref) => {
    if (ref.dead || !ref.b || (ref.sheet ?? on).toLowerCase() !== tab.toLowerCase()) return ref;
    const a = ref.a;
    const b = ref.b;
    if (a.r === null || b.r === null || a.c === null || b.c === null) return ref;
    const lo = Math.min(a.r, b.r);
    const hi = Math.max(a.r, b.r);
    if (lo !== g.body || hi !== g.from || Math.min(a.c, b.c) < g.c0 || Math.max(a.c, b.c) > g.c1) return ref;
    return a.r >= b.r ? { ...ref, a: { ...a, r: g.to } } : { ...ref, b: { ...b, r: g.to } };
  });
}

/** A tab's tables once rows or columns were put in or taken out there: their blocks and column formulas move with their cells. */
export function shiftTables(list: SheetTable[], tabName: string, l: Lines): SheetTable[] | null {
  const out: SheetTable[] = [];
  for (const t of list) {
    const x = tableRect(t);
    if (!x) continue;
    const range = shiftFormula(`=${t.range}`, tabName, { tab: tabName, ...l }).slice(1);
    const y = tableRect({ range });
    if (!y) continue;
    let cols: Record<string, TableCol> | undefined;
    for (const [k, col] of Object.entries(t.cols ?? {})) {
      const c = x.c0 + Number(k);
      const nc = l.axis === "col" ? lineAfter(c, l) : c;
      if (nc === null || nc < y.c0 || nc > y.c1) continue;
      const next: TableCol = { ...(col.fmt ? { fmt: col.fmt } : {}) };
      if (col.f && typeof col.at === "number") {
        // kept on a row of its body that stays, then moved with the cells
        let keep = -1;
        for (let r = x.r0 + 1; r <= x.r1 && keep < 0; r++) if (l.axis === "col" || lineAfter(r, l) !== null) keep = r;
        if (keep >= 0) {
          const f = shiftFormula(moveFormula(col.f, keep - col.at, 0), tabName, { tab: tabName, ...l });
          const at = l.axis === "row" ? lineAfter(keep, l) : keep;
          if (at !== null && !f.includes("#REF!")) {
            next.f = f;
            next.at = at;
          }
        }
      }
      if (next.f || next.fmt) (cols ??= {})[String(nc - y.c0)] = next;
    }
    out.push({ name: t.name, range: rectName(y), ...(cols ? { cols } : {}) });
  }
  return out.length ? out : null;
}

/** A copy's name for a table, free of every name taken: Orders2, Orders3… */
export function copyTableName(name: string, taken: string[]): string {
  const low = new Set(taken.map((s) => s.toLowerCase()));
  const base = name.replace(/\d+$/, "") || name;
  for (let n = 2; n < 1000; n++) if (!low.has(`${base}${n}`.toLowerCase())) return `${base}${n}`;
  return `${base}${Date.now() % 10000}`;
}

/** A name for a new table no table or named range has: Table1, Table2… */
export function freshTableName(taken: string[], base = "Table"): string {
  const low = new Set(taken.map((s) => s.toLowerCase()));
  for (let n = 1; n < 1000; n++) if (!low.has(`${base}${n}`.toLowerCase())) return `${base}${n}`;
  return `${base}${Date.now() % 10000}`;
}

/** The block of filled cells around a cell, as Excel's and Google Sheets' Make a table finds it; null when it is empty. */
export function regionAround(r: number, c: number, filled: (r: number, c: number) => boolean, used: { rows: number; cols: number }): Rect | null {
  if (!filled(r, c)) return null;
  let x: Rect = { r0: r, c0: c, r1: r, c1: c };
  const rowHas = (rr: number, c0: number, c1: number) => {
    for (let cc = Math.max(0, c0); cc <= Math.min(c1, used.cols - 1); cc++) if (filled(rr, cc)) return true;
    return false;
  };
  const colHas = (cc: number, r0: number, r1: number) => {
    for (let rr = Math.max(0, r0); rr <= Math.min(r1, used.rows - 1); rr++) if (filled(rr, cc)) return true;
    return false;
  };
  for (let k = 0; k < 400; k++) {
    let grew = false;
    if (x.r0 > 0 && rowHas(x.r0 - 1, x.c0 - 1, x.c1 + 1)) (x = { ...x, r0: x.r0 - 1 }), (grew = true);
    if (x.r1 < Math.min(MAX_ROWS, used.rows) - 1 && rowHas(x.r1 + 1, x.c0 - 1, x.c1 + 1)) (x = { ...x, r1: x.r1 + 1 }), (grew = true);
    if (x.c0 > 0 && colHas(x.c0 - 1, x.r0, x.r1)) (x = { ...x, c0: x.c0 - 1 }), (grew = true);
    if (x.c1 < Math.min(MAX_COLS, used.cols) - 1 && colHas(x.c1 + 1, x.r0, x.r1)) (x = { ...x, c1: x.c1 + 1 }), (grew = true);
    if (!grew) break;
  }
  return x;
}

/** Why a block cannot be a table on a tab, or null. */
export function tableProblem(list: SheetTable[] | undefined, x: Rect, name: string, otherNames: string[], merges: Rect[]): string | null {
  if (x.r1 <= x.r0) return "A table takes a header row and at least one row under it.";
  const bad = nameProblem(name);
  if (bad) return bad;
  if (otherNames.some((n) => n.toLowerCase() === name.toLowerCase())) return `There is already a table or named range called ${name}.`;
  const on = tablesOf(list).find((e) => meet(e.x, x));
  if (on) return `${rectName(x)} runs into the table ${on.t.name} (${on.t.range}).`;
  const m = merges.find((y) => meet(y, x));
  if (m) return `${rectName(x)} holds merged cells (${rectName(m)}). Unmerge them first.`;
  if ((list?.length ?? 0) >= MAX_TABLES) return `A tab holds up to ${MAX_TABLES} tables.`;
  return null;
}

/** A table's columns in words for an agent: each header with its letter, formula and look. */
export function tableWords(t: SheetTable, raw: (r: number, c: number) => string): { line: string; cols: { name: string; column: string; formula?: string; format?: string }[] } {
  const x = tableRect(t)!;
  const heads = headerNames(t, raw);
  const nf: Record<string, string> = { num: "number", cur: "currency", pct: "percent", date: "date", time: "time", datetime: "date and time", text: "text" };
  const cols = heads.map((h, i) => {
    const col = t.cols?.[String(i)];
    const f = columnFormula(col, x.r0 + 1);
    return { name: h, column: colName(x.c0 + i), ...(f ? { formula: f } : {}), ...(col?.fmt?.nf ? { format: nf[col.fmt.nf] ?? col.fmt.nf } : {}) };
  });
  const rows = x.r1 - x.r0;
  const line = `- ${t.name}: ${t.range}, headers on row ${x.r0 + 1}, ${rows} row${rows === 1 ? "" : "s"}; columns ${cols.map((c) => `${c.name} (${c.column}${c.formula ? `, every row ${c.formula}` : ""}${c.format ? `, ${c.format}` : ""})`).join(", ")}`;
  return { line, cols };
}


/** A formula with a table's name changed wherever it names the table (Orders, Orders[Total]); anything else as it was. */
export function renameTableIn(raw: string, from: string, to: string): string {
  if (!isFormula(raw) || !raw.toLowerCase().includes(from.toLowerCase())) return raw;
  const src = raw.slice(1);
  let toks;
  try {
    toks = tokenize(src);
  } catch (e) {
    if (e instanceof ParseError) return raw;
    throw e;
  }
  let out = "";
  let at = 0;
  const low = from.toLowerCase();
  for (const t of toks) {
    if (t.k !== "name") continue;
    const v = t.v;
    const head = v.includes("[") ? v.slice(0, v.indexOf("[")) : v;
    if (head.toLowerCase() !== low) continue;
    out += src.slice(at, t.pos) + to + v.slice(head.length);
    at = t.end;
  }
  return at === 0 ? raw : "=" + out + src.slice(at);
}

/**
 * A new table on a block: a column whose rows all hold one formula, as copies of it, keeps it as the column's; one
 * whose rows all show one number look keeps that. raw and look read a cell as typed and as it looks.
 */
export function newTable(name: string, x: Rect, raw: (r: number, c: number) => string, look: (r: number, c: number) => CellFmt): SheetTable {
  const cols: Record<string, TableCol> = {};
  for (let c = x.c0; c <= x.c1; c++) {
    const col: TableCol = {};
    const first = raw(x.r0 + 1, c);
    let same = isFormula(first);
    for (let r = x.r0 + 2; r <= x.r1 && same; r++) if (raw(r, c) !== moveFormula(first, r - x.r0 - 1, 0)) same = false;
    if (same) Object.assign(col, { f: first, at: x.r0 + 1 });
    const seen = new Set<string>();
    for (let r = x.r0 + 1; r <= x.r1; r++) {
      const f = look(r, c);
      seen.add(JSON.stringify(Object.fromEntries(COLUMN_LOOKS.filter((k) => f[k] !== undefined).map((k) => [k, f[k]]))));
    }
    const fmt = seen.size === 1 ? cleanFmt(JSON.parse([...seen][0]) as object, false) : null;
    if (fmt) col.fmt = fmt;
    if (col.f || col.fmt) cols[String(c - x.c0)] = col;
  }
  return { name, range: rectName(x), ...(Object.keys(cols).length ? { cols } : {}) };
}

/**
 * A number look set on cells inside one table's rows, as the columns': the tables after, the look's keys that went to
 * the columns, and the table's block; null when the block is not inside one table's rows or the look has none.
 */
export function columnLook(list: SheetTable[] | undefined, rect: Rect, patch: Record<string, unknown>): { tables: SheetTable[]; keys: string[]; x: Rect } | null {
  const keys = Object.keys(patch).filter((k) => (COLUMN_LOOKS as readonly string[]).includes(k));
  if (!keys.length || !list?.length) return null;
  const at = tablesOf(list).find((e) => rect.r0 > e.x.r0 && rect.r1 <= e.x.r1 && rect.c0 >= e.x.c0 && rect.c1 <= e.x.c1);
  if (!at) return null;
  const cols = { ...(at.t.cols ?? {}) };
  for (let c = rect.c0; c <= rect.c1; c++) {
    const i = String(c - at.x.c0);
    const next: Record<string, unknown> = { ...(cols[i]?.fmt ?? {}) };
    for (const k of keys) {
      const v = patch[k];
      if (v === null || v === undefined || v === false) delete next[k];
      else next[k] = v;
    }
    const fmt = cleanFmt(next, false);
    const col: TableCol = { ...(cols[i] ?? {}) };
    if (fmt) col.fmt = fmt;
    else delete col.fmt;
    if (col.f || col.fmt) cols[i] = col;
    else delete cols[i];
  }
  const tables = list.map((t) => {
    if (t !== at.t) return t;
    const { cols: _c, ...rest } = t;
    void _c;
    return Object.keys(cols).length ? { ...rest, cols } : rest;
  });
  return { tables, keys, x: at.x };
}
